import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PRIORITIES, PRIORITY_LABEL } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Trash2 } from "lucide-react";
import ConfirmDialog from "@/components/common/ConfirmDialog";

export default function SLAAdminPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", priority: "P3", first_response_minutes: 60, resolution_minutes: 24 * 60, active: true });
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/sla-policies").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const name = form.name.trim();
    if (!name) return toast.error("Name is required");
    try {
      await api.post("/sla-policies", { ...form, name });
      toast.success("SLA policy created");
      setForm({ name: "", priority: "P3", first_response_minutes: 60, resolution_minutes: 24 * 60, active: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (p) => { await api.patch(`/sla-policies/${p.id}`, { active: !p.active }); load(); };

  const startEdit = (p) => setEditing({ ...p });
  const saveEdit = async () => {
    const name = (editing.name || "").trim();
    if (!name) return toast.error("Name is required");
    try {
      await api.patch(`/sla-policies/${editing.id}`, {
        name,
        priority: editing.priority,
        first_response_minutes: Number(editing.first_response_minutes),
        resolution_minutes: Number(editing.resolution_minutes),
        active: editing.active,
      });
      toast.success("Policy updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doDelete = async (id) => {
    try {
      await api.delete(`/sla-policies/${id}`);
      setConfirmDelete(null);
      toast.success("Deleted successfully");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-sla-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">SLA policies</h1>
        <p className="text-sm text-muted-foreground">Configure response and resolution targets per priority. Applied automatically once a ticket is approved for support.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
        <div className="rounded-xl border border-border bg-card/70 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">First response</th>
                <th className="text-left px-4 py-3">Resolution</th>
                <th className="text-left px-4 py-3">Active</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} data-row data-testid={`sla-row-${p.priority}-${p.id.slice(0, 8)}`} className="border-b border-border/70">
                  <td className="px-4 py-3">{p.name}</td>
                  <td className="px-4 py-3 font-mono">{p.priority}</td>
                  <td className="px-4 py-3">{p.first_response_minutes} min</td>
                  <td className="px-4 py-3">{p.resolution_minutes} min</td>
                  <td className="px-4 py-3"><Switch checked={p.active} onCheckedChange={() => toggle(p)} data-testid={`sla-toggle-${p.id}`} /></td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="outline" size="sm" onClick={() => startEdit(p)} data-testid={`sla-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>{" "}
                    <Button variant="outline" size="sm" onClick={() => setConfirmDelete(p)} data-testid={`sla-delete-${p.id}`} className="text-rose-300 border-rose-500/30 hover:bg-rose-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-border bg-card/70 p-4 space-y-3 h-fit">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">New policy</div>
          <div><Label>Name<span className="ml-1 text-rose-500">*</span></Label><Input data-testid="sla-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="sla-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABEL[p]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>First response (min)</Label><Input data-testid="sla-first" type="number" value={form.first_response_minutes} onChange={(e) => setForm({ ...form, first_response_minutes: Number(e.target.value) })} /></div>
          <div><Label>Resolution (min)</Label><Input data-testid="sla-resolution" type="number" value={form.resolution_minutes} onChange={(e) => setForm({ ...form, resolution_minutes: Number(e.target.value) })} /></div>
          <Button data-testid="sla-create" onClick={submit} className="w-full bg-[#d4af37] text-black hover:bg-[#e5c158]">Create policy</Button>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-sla-dialog">
          <DialogHeader><DialogTitle>Edit SLA policy</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Name<span className="ml-1 text-rose-500">*</span></Label><Input data-testid="edit-sla-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div>
                <Label>Priority</Label>
                <Select value={editing.priority} onValueChange={(v) => setEditing({ ...editing, priority: v })}>
                  <SelectTrigger data-testid="edit-sla-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABEL[p]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>First response (min)</Label><Input data-testid="edit-sla-first" type="number" value={editing.first_response_minutes} onChange={(e) => setEditing({ ...editing, first_response_minutes: e.target.value })} /></div>
              <div><Label>Resolution (min)</Label><Input data-testid="edit-sla-resolution" type="number" value={editing.resolution_minutes} onChange={(e) => setEditing({ ...editing, resolution_minutes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="edit-sla-save" onClick={saveEdit} className="bg-[#d4af37] text-black hover:bg-[#e5c158]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={confirmDelete ? `Delete "${confirmDelete.name}"?` : "Delete?"}
        description="Soft delete — the policy will be hidden. Policies attached to open tickets cannot be deleted."
        confirmLabel="Delete"
        destructive
        onConfirm={() => confirmDelete && doDelete(confirmDelete.id)}
        testid="confirm-delete-sla"
      />
    </div>
  );
}
